/**
 * Element-Menü (CC_05 Blocks 6/7 + CC_06 Blocks 2–5, D-30/D-35/F1–F4). One
 * on-select action island at the selected element — the single place for
 * linking, image upload, Bilderrahmen size and the preview-anchor. Host-side
 * only (D-04): public Excalidraw API + scene customData, never packages/.
 *
 * Contents by selection (Mockup v2):
 *   - Shape/Text, no link → „Link hinzufügen" (externe URL / interne Datei) und
 *     „Bild hochladen" (freistehendes Scene-Asset).
 *   - Shape/Text, link set → öffnen · bearbeiten · entfernen (+ kind-Badge).
 *   - Frame (sectional) → Verknüpfen (intern) · Bild hochladen · Vorschau-Anker
 *     · Umbenennen.
 *   - Frame (Bilderrahmen) → Bild ersetzen · Größe (Original/Einpassen/Füllen)
 *     · Vorschau-Anker · Umbenennen.
 */

import { useEffect, useRef, useState } from "react";

import type {
  ExcalidrawElement,
  NonDeletedExcalidrawElement,
} from "@excalidraw/element/types";
import type { ExcalidrawImperativeAPI } from "@excalidraw/excalidraw/types";

import { ryfeApi } from "../api";
import { openFileById } from "../open-file";
import {
  getDisplayMode,
  getElementLinkInfo,
  getFrameState,
  getImageFit,
  getSceneLinkMode,
  isFrameEl,
  insertSceneImage,
  removeElementLink,
  renameFrame,
  setDisplayMode,
  setSceneLinkMode,
  setElementExternalLink,
  setElementInternalLink,
  setFrameImage,
  setFrameImageFit,
  updateFrameMeta,
} from "../scene-links";

import { SceneBrowserDialog } from "./SceneBrowserDialog";

import "./ryfe.scss";

import type {
  DisplayMode,
  ElementLinkInfo,
  ImageFit,
  UploadedImage,
} from "../scene-links";

const kindLabel: Record<string, string> = {
  md: "MD",
  board: "Board",
  list: "Liste",
};

const FITS: { fit: ImageFit; label: string }[] = [
  { fit: "original", label: "Original" },
  { fit: "fit", label: "Einpassen" },
  { fit: "fill", label: "Füllen" },
];

// CC_10 Block 1 (D-54) — the in-scene display modes offered in the menu.
const DISPLAY_MODES: { mode: DisplayMode; label: string }[] = [
  { mode: "scaleWithCanvas", label: "Mit Canvas" },
  { mode: "keepReadable", label: "Lesbar" },
  { mode: "noScale", label: "1:1" },
];

/** Read a File into {dataURL, natural size} — needs the browser Image loader. */
function readImage(file: File): Promise<Omit<UploadedImage, "created">> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = () => reject(new Error("read failed"));
    reader.onload = () => {
      const dataURL = String(reader.result);
      const im = new Image();
      im.onload = () =>
        resolve({
          dataURL,
          mimeType: file.type,
          naturalWidth: im.naturalWidth || 320,
          naturalHeight: im.naturalHeight || 200,
        });
      im.onerror = () =>
        resolve({
          dataURL,
          mimeType: file.type,
          naturalWidth: 320,
          naturalHeight: 200,
        });
      im.src = dataURL;
    };
    reader.readAsDataURL(file);
  });
}

export const ElementMenu = ({
  wsId,
  api,
  currentFileId,
  selected,
  wsCandidates,
  mainFrameId,
  setMainFrame,
  uploadStamp,
}: {
  wsId: string;
  api: ExcalidrawImperativeAPI;
  currentFileId: string;
  selected: NonDeletedExcalidrawElement | ExcalidrawElement;
  wsCandidates: string[];
  // CC_06 — preview-anchor (F3) is the file-level main-frame pointer.
  mainFrameId: string | null;
  setMainFrame: (id: string | null) => void;
  uploadStamp: number;
}) => {
  const [browsing, setBrowsing] = useState(false);
  const [subOpen, setSubOpen] = useState(false);
  const [targetKind, setTargetKind] = useState<string | null>(null);
  const fileInput = useRef<HTMLInputElement | null>(null);

  const info: ElementLinkInfo = getElementLinkInfo(selected);
  const isFrame = isFrameEl(selected);
  const fstate = isFrame ? getFrameState(selected, mainFrameId) : null;
  // CC_10 Block 1 (D-54) — the display-mode toggle governs how a HOST-RENDERED
  // preview/embed scales: an embed frame, an internal-linked (preview) element,
  // or a native embeddable. Plain shapes/text render nothing inline, so they
  // don't get the toggle.
  const hostsPreview =
    (isFrame && !!fstate?.link) ||
    info.kind === "internal" ||
    selected.type === "embeddable";
  const displayMode = getDisplayMode(selected);

  useEffect(() => {
    let cancelled = false;
    if (info.kind === "internal") {
      (async () => {
        for (const ws of wsCandidates) {
          try {
            const meta = await ryfeApi.getFile(ws, info.targetFileId);
            if (!cancelled) {
              setTargetKind(meta.kind);
            }
            return;
          } catch {
            /* next workspace */
          }
        }
      })();
    } else {
      setTargetKind(null);
    }
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [info.kind, info.kind === "internal" ? info.targetFileId : ""]);

  const addExternal = () => {
    const url = window.prompt("Externe URL (https://…)");
    if (url) {
      setElementExternalLink(api, selected.id, url.trim());
    }
    setSubOpen(false);
  };
  const pickInternal = (fileId: string, name: string) => {
    setElementInternalLink(api, selected.id, fileId, name);
    setBrowsing(false);
    setSubOpen(false);
  };
  const openLink = () => {
    if (info.kind === "external") {
      window.open(info.url, "_blank", "noopener,noreferrer");
    } else if (info.kind === "internal") {
      void openFileById(wsCandidates, info.targetFileId);
    }
  };
  const rename = () => {
    const name = window.prompt(
      "Frame umbenennen",
      (selected as { name?: string }).name || "",
    );
    if (name !== null) {
      renameFrame(api, selected.id, name);
    }
  };

  const onImageChosen = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (!f) {
      return;
    }
    const img = { ...(await readImage(f)), created: uploadStamp };
    if (isFrame) {
      setFrameImage(api, selected.id, img);
    } else {
      insertSceneImage(api, img, {
        x: (selected as { x: number }).x,
        y: (selected as { y: number }).y,
      });
    }
  };

  const toggleAnchor = () =>
    setMainFrame(fstate?.previewAnchor ? null : selected.id);

  return (
    <div className="ryfe-elmenu" data-testid="element-menu">
      <input
        ref={fileInput}
        type="file"
        accept="image/*"
        hidden
        onChange={onImageChosen}
      />

      {isFrame && (
        <button className="ryfe-elmenu__item" onClick={rename}>
          ✎ Umbenennen
        </button>
      )}

      {/* Frame: Verknüpfen (intern) für einen schlichten Frame (Block 7/CC_06). */}
      {isFrame && fstate && !fstate.link && (
        <button className="ryfe-elmenu__item" onClick={() => setBrowsing(true)}>
          🔗 Verknüpfen (intern)…
        </button>
      )}

      {/* Bild hochladen / ersetzen (F1/F2). */}
      <button
        className="ryfe-elmenu__item"
        onClick={() => fileInput.current?.click()}
      >
        🖼 {isFrame && fstate?.image ? "Bild ersetzen" : "Bild hochladen"}
      </button>

      {/* Bilderrahmen-Größe (F4) — nur bei einem Frame mit Bild. */}
      {isFrame && fstate?.image && (
        <div className="ryfe-elmenu__sizes">
          <span className="ryfe-elmenu__head">GRÖSSE</span>
          <div className="ryfe-elmenu__seg">
            {FITS.map((o) => (
              <button
                key={o.fit}
                className={
                  getImageFit(selected) === o.fit
                    ? "ryfe-elmenu__size is-active"
                    : "ryfe-elmenu__size"
                }
                onClick={() => setFrameImageFit(api, selected.id, o.fit)}
              >
                {o.label}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Vorschau-Anker (F3) — grauer Haken beim aktiven, exklusiv pro Board. */}
      {isFrame && fstate && (
        <button className="ryfe-elmenu__item" onClick={toggleAnchor}>
          ★ Vorschau-Anker
          {fstate.previewAnchor && <span className="ryfe-elmenu__chk">✓</span>}
        </button>
      )}

      {/* CC_10 Block 1 (D-54) — In-Scene-Anzeigemodus für Vorschau/Embed. */}
      {hostsPreview && (
        <div className="ryfe-elmenu__sizes" data-testid="display-mode">
          <span className="ryfe-elmenu__head">ANZEIGE</span>
          <div className="ryfe-elmenu__seg">
            {DISPLAY_MODES.map((o) => (
              <button
                key={o.mode}
                className={
                  displayMode === o.mode
                    ? "ryfe-elmenu__size is-active"
                    : "ryfe-elmenu__size"
                }
                onClick={() => setDisplayMode(api, selected.id, o.mode)}
              >
                {o.label}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Link an einer Form (CC_05 Block 6). */}
      {!isFrame &&
        (info.kind === "none" ? (
          <div className="ryfe-elmenu__wrap">
            <button
              className="ryfe-elmenu__item"
              onClick={() => setSubOpen((o) => !o)}
            >
              🔗 Link hinzufügen ›
            </button>
            {subOpen && (
              <div className="ryfe-elmenu__sub">
                <button className="ryfe-elmenu__item" onClick={addExternal}>
                  Externe URL
                </button>
                <button
                  className="ryfe-elmenu__item"
                  onClick={() => {
                    setBrowsing(true);
                    setSubOpen(false);
                  }}
                >
                  Interne Datei…
                </button>
              </div>
            )}
          </div>
        ) : (
          <div className="ryfe-elmenu__linked">
            <span className="ryfe-elmenu__head">
              Verknüpft
              {info.kind === "internal" && targetKind && (
                <span className="ryfe-elmenu__badge">
                  {kindLabel[targetKind] || targetKind}
                </span>
              )}
            </span>
            {/* R4 (Punkt 15) — nachträglich zwischen Button-Darstellung und
                Dokumenten-Vorschau umschalten (vorher nur beim Anlegen wählbar). */}
            {info.kind === "internal" && (
              <div className="ryfe-elmenu__sizes" data-testid="link-mode">
                <span className="ryfe-elmenu__head">DARSTELLUNG</span>
                <div className="ryfe-elmenu__seg">
                  {(
                    [
                      { mode: "button", label: "Button" },
                      { mode: "preview", label: "Vorschau" },
                    ] as const
                  ).map((o) => (
                    <button
                      key={o.mode}
                      className={
                        getSceneLinkMode(selected) === o.mode
                          ? "ryfe-elmenu__size is-active"
                          : "ryfe-elmenu__size"
                      }
                      onClick={() => setSceneLinkMode(api, selected.id, o.mode)}
                    >
                      {o.label}
                    </button>
                  ))}
                </div>
              </div>
            )}
            <button className="ryfe-elmenu__item" onClick={openLink}>
              ↗ Link öffnen
            </button>
            <button
              className="ryfe-elmenu__item"
              onClick={() =>
                info.kind === "external" ? addExternal() : setBrowsing(true)
              }
            >
              ✎ Link bearbeiten
            </button>
            <button
              className="ryfe-elmenu__item"
              onClick={() => removeElementLink(api, selected.id)}
            >
              ⊘ Link entfernen
            </button>
          </div>
        ))}

      {browsing && (
        <SceneBrowserDialog
          wsId={wsId}
          currentFileId={currentFileId}
          // CC_07 Block 3 (D-39) — the frame/embed target is no longer
          // board-only; a frame may embed a note or a list too, matching the
          // already kind-agnostic element-link path.
          kinds={["board", "md", "list"]}
          title={isFrame ? "Ziel-Datei wählen" : "Interne Datei verknüpfen"}
          onPick={
            isFrame
              ? (fileId, name) => {
                  updateFrameMeta(api, selected.id, {
                    type: "embed",
                    embedTarget: { targetFileId: fileId, targetName: name },
                  });
                  setBrowsing(false);
                }
              : pickInternal
          }
          onClose={() => setBrowsing(false)}
        />
      )}
    </div>
  );
};
